import { Camera, Check, Image, X } from 'lucide-react';
import { Card } from '@/components/Shared/Card';
import { Badge } from '@/components/Shared/Badge';
import type {
  ChecklistProtocolEmployee,
  ChecklistProtocolItem,
} from '@/store/useChecklistProtocolStore';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

interface ProtocolEmployeeCardProps {
  employee: ChecklistProtocolEmployee;
  onViewPhoto: (documentId: string) => void;
  t: Translate;
}

/**
 * One employee's checklists on one day. Shared by the day protocol and the open-items view so
 * an unconfirmed point reads identically in both.
 */
export function ProtocolEmployeeCard({ employee, onViewPhoto, t }: ProtocolEmployeeCardProps) {
  return (
    <Card
      title={employee.employeeName ?? '–'}
      actions={
        <span className="text-[12px] text-slate-500 tabular-nums self-center">
          {t('summary.employeeCounts', {
            completed: employee.completedCount ?? 0,
            open: employee.openCount ?? 0,
          })}
        </span>
      }
    >
      <div className="space-y-4">
        {(employee.tasks ?? []).map((task) => (
          <div key={task.taskId}>
            <div className="text-[13px] font-medium text-slate-900">
              {task.objectName} — {task.taskName}
            </div>
            <div className="mt-1.5 space-y-1">
              {(task.items ?? []).map((item) => (
                <ProtocolItemRow
                  key={`${task.taskId}-${item.checklistItemId}`}
                  item={item}
                  onViewPhoto={onViewPhoto}
                  t={t}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ProtocolItemRow({
  item,
  onViewPhoto,
  t,
}: {
  item: ChecklistProtocolItem;
  onViewPhoto: (documentId: string) => void;
  t: Translate;
}) {
  const done = item.isCompleted ?? false;

  return (
    <div className="flex items-start gap-2 px-2 py-1.5 rounded-md border border-slate-200 bg-slate-50">
      <span
        className={`mt-0.5 shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
          done ? 'bg-[#00A871] border-[#00A871] text-white' : 'bg-white border-slate-300'
        }`}
        aria-hidden
      >
        {done ? <Check className="w-3 h-3" /> : <X className="w-3 h-3 text-slate-300" />}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[13px] text-slate-900">{item.title}</span>
          {item.intervalHint && (
            <span className="text-[11px] text-slate-500">{item.intervalHint}</span>
          )}
          {item.isOrphaned && <Badge variant="default">{t('badges.removed')}</Badge>}
          {item.requiresPhoto && !item.photoDocumentId && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-700">
              <Camera className="w-3 h-3" aria-hidden />
              {t('badges.photoMissing')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[11px] text-slate-500 tabular-nums">
            {item.completedAt
              ? new Date(item.completedAt).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : t('badges.notConfirmed')}
          </span>
          {item.completedBy && (
            <span className="text-[11px] text-slate-500">{item.completedBy}</span>
          )}
          {item.note && (
            <span className="text-[11px] text-slate-600 italic">{item.note}</span>
          )}
          {item.photoDocumentId && (
            <button
              type="button"
              onClick={() => onViewPhoto(item.photoDocumentId!)}
              className="inline-flex items-center gap-1 text-[11px] text-[#00A871] hover:underline"
            >
              <Image className="w-3 h-3" aria-hidden />
              {t('actions.viewPhoto')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
